import { useState } from 'react'
import { api, APIError } from '../lib/api'

interface Props {
  onClose: () => void
  onCreated: () => void
}

export function CreateUserDialog({ onClose, onCreated }: Props) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<'user' | 'manager' | 'admin'>('user')
  const [employeeNumber, setEmployeeNumber] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [generatedPwd, setGeneratedPwd] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const created = await api<{ generated_password: string | null }>(
        '/api/admin/users',
        {
          method: 'POST',
          body: JSON.stringify({
            email,
            full_name: fullName,
            role,
            employee_number: employeeNumber || null,
          }),
        },
      )
      setGeneratedPwd(created.generated_password)
    } catch (err) {
      if (err instanceof APIError && err.status === 409) {
        setError('Email или табельный номер уже используются')
      } else {
        setError('Не удалось создать пользователя')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-xl w-full max-w-md p-6">
        {generatedPwd ? (
          <>
            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-2">
              Пользователь создан
            </h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
              Одноразовый пароль (передайте пользователю):
            </p>
            <code className="block bg-amber-50 dark:bg-amber-900/30 p-3 rounded-md text-sm font-mono text-amber-900 dark:text-amber-100">
              {generatedPwd}
            </code>
            <div className="mt-4 flex justify-end">
              <button
                onClick={onCreated}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm"
              >
                Закрыть
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
              Новый пользователь
            </h2>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
            />
            <input
              type="text"
              placeholder="ФИО"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
            />
            <input
              type="text"
              placeholder="Табельный номер (опц.)"
              value={employeeNumber}
              onChange={(e) => setEmployeeNumber(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'user' | 'manager' | 'admin')}
              className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
            >
              <option value="user">user</option>
              <option value="manager">manager</option>
              <option value="admin">admin</option>
            </select>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
              >
                {busy ? 'Создаю…' : 'Создать'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
